import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { FaPlug, FaSave, FaSyncAlt, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { integrationsAPI } from '../services/api';

// Every integration is an optional, purchased add-on: "Purchased" is the commercial
// entitlement (Commercial Setup), "Enabled" switches it on for this installation, and
// each feature (employee / product / API sync…) is opt-in on top. The connection itself
// is configured on the Integration Hub (IntegrationCard below). Secrets are write-only.

const AUTH_LABELS = { api_key: 'API key header', bearer: 'Bearer token', basic: 'Basic (user / password)' };

// Which path fields matter for which feature.
const PATH_FIELDS = [
  { field: 'health_path', label: 'Health-check Path', show: (it) => ['aiventra_qms', 'aiventra_dms'].includes(it.key) },
  { field: 'employee_path', label: 'Employee List Path', show: (it) => 'employee_sync' in it.feature_labels },
  { field: 'product_path', label: 'Product (Material) Path', show: (it) => 'product_sync' in it.feature_labels },
  { field: 'api_path', label: 'API (Substance) Path', show: (it) => 'api_sync' in it.feature_labels },
  { field: 'webhook_path', label: 'Webhook / Event Path', show: (it) => ['aiventra_lms', 'aiventra_qms', 'aiventra_dms'].includes(it.key) }
];

const MAP_FIELDS = [
  { field: 'employee_map', label: 'Employee field mapping', feature: 'employee_sync', hint: 'employeeId, email, firstName, lastName, department, designation' },
  { field: 'product_map', label: 'Product field mapping', feature: 'product_sync', hint: 'product_code, product_name, dosage_form, batch_size, batch_size_unit …' },
  { field: 'api_map', label: 'API field mapping', feature: 'api_sync', hint: 'api_code, api_name, max_daily_dose, therapeutic_dose …' }
];

const toDraft = (item) => ({
  purchased: item.purchased,
  enabled: item.enabled,
  features: { ...item.features },
  settings: {
    ...item.settings,
    employee_map: item.settings.employee_map ? JSON.stringify(item.settings.employee_map, null, 2) : undefined,
    product_map: item.settings.product_map ? JSON.stringify(item.settings.product_map, null, 2) : undefined,
    api_map: item.settings.api_map ? JSON.stringify(item.settings.api_map, null, 2) : undefined
  },
  secrets: { api_key: '', password: '' }
});

const hint = { fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', margin: '4px 0 0' };
const pill = { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', borderRadius: '8px', background: 'var(--bg-color)' };

const DMS_PLATFORMS = [
  { value: 'aiventra', label: 'AiventraDMS' },
  { value: 'veeva', label: 'Veeva Vault' },
  { value: 'mastercontrol', label: 'MasterControl' },
  { value: 'sharepoint', label: 'SharePoint / OneDrive' },
  { value: 'documentum', label: 'OpenText Documentum' },
  { value: 'custom', label: 'Other REST DMS' }
];

// allowPurchase: Commercial Setup may tick "Purchased"; the Integration Hub shows it read-only.
export const IntegrationCard = ({ item, onSaved, allowPurchase = false }) => {
  const [draft, setDraft] = useState(() => toDraft(item));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => { setDraft(toDraft(item)); }, [item]);

  const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  const setSetting = (field, value) => setDraft((prev) => ({ ...prev, settings: { ...prev.settings, [field]: value } }));
  const setSecret = (field, value) => setDraft((prev) => ({ ...prev, secrets: { ...prev.secrets, [field]: value } }));
  const setFeature = (field, value) => setDraft((prev) => ({ ...prev, features: { ...prev.features, [field]: value } }));

  const authType = draft.settings.auth_type || item.auth_types[0];
  const active = draft.purchased && draft.enabled;

  const save = async () => {
    setSaving(true);
    try {
      const settings = { ...draft.settings };
      MAP_FIELDS.forEach(({ field }) => { if (settings[field] === undefined) delete settings[field]; });
      const payload = { enabled: draft.enabled, features: draft.features, settings, secrets: draft.secrets };
      if (allowPurchase) payload.purchased = draft.purchased;
      const res = await integrationsAPI.save(item.key, payload);
      toast.success(res?.data?.message || `${item.label} saved`);
      onSaved(res?.data?.data);
    } catch (error) {
      toast.error(error?.response?.data?.message || `Failed to save ${item.label}`);
    } finally {
      setSaving(false);
    }
  };

  const clearSecret = async (field) => {
    if (!window.confirm(`Remove the stored ${field === 'password' ? 'password' : 'key/token'} for ${item.label}?`)) return;
    try {
      const res = await integrationsAPI.save(item.key, { secrets: { [field]: null } });
      toast.success('Removed');
      onSaved(res?.data?.data);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to remove');
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const res = await integrationsAPI.test(item.key);
      const result = res?.data?.data || {};
      (result.ok ? toast.success : toast.error)(result.status || 'Test finished');
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const testOk = item.last_test_status?.startsWith('OK');

  return (
    <div style={{ border: active ? '2px solid var(--primary-color)' : '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', background: 'var(--card-bg)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <strong style={{ fontSize: 'var(--fs-lg)' }}>{item.label}</strong>
          <span className={`badge ${active ? 'badge-success' : 'badge-secondary'}`} style={{ marginLeft: '8px' }}>{active ? 'Active' : draft.purchased ? 'Purchased — off' : 'Not purchased'}</span>
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>{item.category}</div>
          <p style={{ ...hint, fontSize: 'var(--fs-sm)' }}>{item.description}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {allowPurchase && (
            <label style={pill}>
              <input type="checkbox" checked={draft.purchased} onChange={(e) => set({ purchased: e.target.checked })} style={{ width: '18px', height: '18px' }} />
              Purchased
            </label>
          )}
          <label style={{ ...pill, opacity: draft.purchased ? 1 : 0.5 }}>
            <input type="checkbox" checked={draft.enabled} disabled={!draft.purchased} onChange={(e) => set({ enabled: e.target.checked })} style={{ width: '18px', height: '18px' }} />
            Enabled
          </label>
        </div>
      </div>

      {!draft.purchased && !allowPurchase && (
        <div style={{ ...pill, fontSize: 'var(--fs-sm)', border: '1px solid var(--warning-color, #d97706)' }}>
          Not purchased for this installation — a Super Admin ticks it in&nbsp;<Link to="/commercial-setup">Commercial Setup → App Integrations</Link>&nbsp;first.
        </div>
      )}
      <fieldset disabled={!draft.purchased} style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', opacity: draft.purchased ? 1 : 0.6 }}>
        <div>
          <label className="form-label">Features</label>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            {Object.entries(item.feature_labels).map(([feature, label]) => (
              <label key={feature} style={pill}>
                <input type="checkbox" checked={!!draft.features[feature]} onChange={(e) => setFeature(feature, e.target.checked)} style={{ width: '18px', height: '18px' }} />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label className="form-label">Base URL</label>
            <input className="form-control" placeholder={item.key === 'sap' ? 'https://my-sap-host:44300' : 'https://lms.example.com'} value={draft.settings.base_url || ''} onChange={(e) => setSetting('base_url', e.target.value)} />
          </div>
          {item.auth_types.length > 1 && (
            <div className="form-group">
              <label className="form-label">Authentication</label>
              <select className="form-control" value={authType} onChange={(e) => setSetting('auth_type', e.target.value)}>
                {item.auth_types.map((type) => <option key={type} value={type}>{AUTH_LABELS[type]}</option>)}
              </select>
            </div>
          )}
          {authType === 'basic' ? (
            <>
              <div className="form-group">
                <label className="form-label">Username</label>
                <input className="form-control" autoComplete="off" value={draft.settings.username || ''} onChange={(e) => setSetting('username', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input className="form-control" type="password" autoComplete="new-password" placeholder={item.secrets.password?.set ? `Stored (${item.secrets.password.masked}) — leave blank to keep` : 'Not set'} value={draft.secrets.password} onChange={(e) => setSecret('password', e.target.value)} />
                {item.secrets.password?.set && <button type="button" className="btn btn-sm btn-link" style={{ padding: 0 }} onClick={() => clearSecret('password')}>Remove stored password</button>}
              </div>
            </>
          ) : (
            <div className="form-group">
              <label className="form-label">{authType === 'bearer' ? 'Bearer Token' : 'API Key'}</label>
              <input className="form-control" type="password" autoComplete="new-password" style={{ fontFamily: 'monospace' }} placeholder={item.secrets.api_key?.set ? `Stored (${item.secrets.api_key.masked}) — leave blank to keep` : 'Not set'} value={draft.secrets.api_key} onChange={(e) => setSecret('api_key', e.target.value)} />
              {item.secrets.api_key?.set && <button type="button" className="btn btn-sm btn-link" style={{ padding: 0 }} onClick={() => clearSecret('api_key')}>Remove stored key</button>}
            </div>
          )}
          {authType === 'api_key' && (
            <div className="form-group">
              <label className="form-label">API Key Header</label>
              <input className="form-control" value={draft.settings.api_key_header || ''} onChange={(e) => setSetting('api_key_header', e.target.value)} />
            </div>
          )}
          {item.key === 'aiventra_dms' && (
            <div className="form-group">
              <label className="form-label">DMS Platform</label>
              <select className="form-control" value={draft.settings.platform || 'aiventra'} onChange={(e) => setSetting('platform', e.target.value)}>
                {DMS_PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          )}
          {item.key === 'aiventra_lms' && (
            <div className="form-group">
              <label className="form-label">Automatic sync every (minutes)</label>
              <input className="form-control" type="number" min="15" step="15" value={draft.settings.auto_sync_minutes || '60'} onChange={(e) => setSetting('auto_sync_minutes', e.target.value)} />
              <p style={hint}>Used when "Automatic linked sync" is ticked. Minimum 15.</p>
            </div>
          )}
          {item.key === 'sap' && (
            <div className="form-group">
              <label className="form-label">SAP Client (sap-client)</label>
              <input className="form-control" placeholder="e.g. 100" value={draft.settings.sap_client || ''} onChange={(e) => setSetting('sap_client', e.target.value)} />
            </div>
          )}
        </div>

        <div>
          <button type="button" className="btn btn-sm btn-outline" onClick={() => setShowAdvanced((v) => !v)}>
            {showAdvanced ? 'Hide' : 'Show'} paths &amp; field mapping
          </button>
        </div>
        {showAdvanced && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
              {PATH_FIELDS.filter((p) => p.show(item)).map((p) => (
                <div key={p.field} className="form-group">
                  <label className="form-label">{p.label}</label>
                  <input className="form-control" style={{ fontFamily: 'monospace', fontSize: 'var(--fs-sm)' }} value={draft.settings[p.field] || ''} onChange={(e) => setSetting(p.field, e.target.value)} />
                </div>
              ))}
            </div>
            {item.key !== 'aiventra_lms' && (
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                {MAP_FIELDS.filter((m) => m.feature in item.feature_labels).map((m) => (
                  <div key={m.field} className="form-group">
                    <label className="form-label">{m.label}</label>
                    <textarea className="form-control" rows={6} style={{ fontFamily: 'monospace', fontSize: 'var(--fs-sm)' }} value={draft.settings[m.field] || ''} onChange={(e) => setSetting(m.field, e.target.value)} />
                    <p style={hint}>JSON: our field → source field path (dots for nested, e.g. <code>to_Description.results.0.ProductDescription</code>). Fields: {m.hint}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </fieldset>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {item.last_test_status && (
            <span style={{ color: testOk ? 'var(--success-color, #16a34a)' : 'var(--danger-color, #dc2626)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {testOk ? <FaCheckCircle /> : <FaTimesCircle />} Last test {new Date(item.last_test_at).toLocaleString()}: {item.last_test_status}
            </span>
          )}
          {item.last_sync_at && (
            <span>Last sync {new Date(item.last_sync_at).toLocaleString()}{item.last_sync_summary ? ` — ${Object.entries(item.last_sync_summary).filter(([k]) => k !== 'source').map(([k, v]) => `${k}: ${v}`).join(', ')}` : ''}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" className="btn btn-outline" onClick={test} disabled={testing || !draft.purchased} title="Uses the saved settings — save first">
            <FaSyncAlt className={testing ? 'fa-spin' : ''} /> {testing ? 'Testing…' : 'Test Connection'}
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            <FaSave /> {saving ? 'Saving…' : `Save ${item.label}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// Commercial Setup → App Integrations: only the commercial entitlement per add-on.
// Connection, features and sync live on the Integration Hub.
const PurchaseRow = ({ item, onSaved }) => {
  const [purchased, setPurchased] = useState(item.purchased);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPurchased(item.purchased); }, [item]);
  const save = async (value) => {
    setPurchased(value);
    setSaving(true);
    try {
      const res = await integrationsAPI.save(item.key, { purchased: value });
      toast.success(`${item.label}: ${value ? 'purchased' : 'not purchased'}`);
      onSaved(res?.data?.data);
    } catch (error) {
      setPurchased(item.purchased);
      toast.error(error?.response?.data?.message || `Failed to save ${item.label}`);
    } finally {
      setSaving(false);
    }
  };
  const active = item.purchased && item.enabled;
  return (
    <label style={{ ...pill, justifyContent: 'space-between', border: '1px solid var(--border-color)' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input type="checkbox" checked={purchased} disabled={saving} onChange={(e) => save(e.target.checked)} style={{ width: '18px', height: '18px' }} />
        <span>
          <strong>{item.label}</strong>
          <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>{item.category}</span>
        </span>
      </span>
      <span className={`badge ${active ? 'badge-success' : 'badge-secondary'}`}>{active ? 'Active' : purchased ? 'Purchased — off' : 'Not purchased'}</span>
    </label>
  );
};

const AppIntegrationsEditor = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await integrationsAPI.list();
      setItems(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load app integrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSaved = (updated) => {
    if (updated?.key) setItems((prev) => prev.map((it) => (it.key === updated.key ? updated : it)));
    else load();
  };

  return (
    <div className="form-section">
      <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FaPlug /> App Integrations</h3>
      <p style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginTop: '-6px' }}>
        Optional add-ons per client. Tick <strong>Purchased</strong> for what this client bought (saves at once). Connection details, features,
        linked syncing and sync history are set on the <Link to="/integrations">Integration Hub</Link>.
      </p>
      {loading ? (
        <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>Loading integrations…</div>
      ) : (
        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          {items.map((item) => <PurchaseRow key={item.key} item={item} onSaved={handleSaved} />)}
        </div>
      )}
    </div>
  );
};

export default AppIntegrationsEditor;
